import * as React from 'react';
import { toast } from '@/components/ui/toast';
import { getMainDefinition } from '@apollo/client/utilities'
import { WebSocketLink } from '@apollo/client/link/ws'

import {
    ApolloClient,
    InMemoryCache,
    ApolloProvider,
    split,
    HttpLink,
    ApolloLink,
} from "@apollo/client";

import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

//import { getMainDefinition } from '@apollo/client/utilities'
//import { WebSocketLink } from '@apollo/client/link/ws'

const AUTH_ROUTE_PATTERNS = [
  /^\/login(?:\/|$)/i,
  /^\/loginad(?:\/|$)/i,
  /^\/support\/lostpassword(?:\/|$)/i,
  /^\/support\/resetpassword\/.+/i,
  /^\/support\/temp-login\/.+/i
];

let pageTeardownStarted = false;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    pageTeardownStarted = true;
  });
  window.addEventListener('pagehide', () => {
    pageTeardownStarted = true;
  });
}

export const isPageTeardownNetworkError = (error: unknown) => {
  if (!pageTeardownStarted) {
    return false;
  }
  const candidate = error as any;
  const message = `${candidate?.networkError?.message || candidate?.message || candidate || ''}`;
  return /failed to fetch|load failed|network\s*error/i.test(message);
};

const clearAuthorizationCookie = () => {
  try {
    document.cookie = 'Authorization=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  } catch (_error) {
    // no-op
  }
};

const isSilentAuthRoute = () => {
  const pathname = `${window?.location?.pathname || ''}`.toLowerCase();
  return AUTH_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
};

export const isAuthErrorMessage = (message: string) => {
  const normalized = `${message || ''}`.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes('token expired') ||
    normalized.includes('authentication required') ||
    normalized.includes('invalid token') ||
    normalized.includes('uuid token expired') ||
    normalized.includes('legacy token expired')
  );
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      //Handle GraphQL errors

      const message = `${error?.message || ''}`;
      if (isAuthErrorMessage(message)){        
        if (isSilentAuthRoute()) {
          clearAuthorizationCookie();
          return;
        }
        window.location.href = "/login"
        return
      }
      
      toast.error(message);
      
        
    });
  }
  if (networkError) {
    if (isPageTeardownNetworkError(networkError)) {
      return;
    }
    // Handle network errors
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      if (isSilentAuthRoute()) {
        clearAuthorizationCookie();
        return;
      }
      window.location.href = "/login";
      return;
    }
    if ('statusCode' in networkError && networkError.statusCode === 403) {
      toast.error('Access denied.');
      return;
    }
    toast.error('Network error occurred. Please try again.');
  }
});


const createWebConnection = (server: any, authRef: React.MutableRefObject<string>) =>{

  const httpLink = new HttpLink({
    uri: server.http
  });

  // Use setContext to dynamically inject auth header from ref
  const authLink = setContext((_, { headers }) => {
    const token = authRef.current;
    if (!token) return { headers };

    return {
      headers: {
        ...headers,
        authorization: `JWT ${token}`
      }
    };
  });

  const wsLink = new WebSocketLink({
    uri: server.ws,
    options: {
      reconnect: true,
      connectionParams: () => {
        const token = authRef.current;
        if (!token) return undefined;
        return {
          headers: { authorization: `JWT ${token}` }
        };
      }
    }
  });

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    authLink.concat(httpLink),
  );


  return splitLink
}


const ApolloAuthContext = React.createContext (null) as any

export const Apollo = (params: any) =>{
    const {children, server,  auth} = params
    const authRef = React.useRef(auth);
    const hasAuth = !!auth;

    // Keep auth current during render. Child effects can issue a request before
    // a parent effect runs (notably temporary compliance login), so updating
    // this ref in an effect creates one unauthenticated request on token change.
    authRef.current = auth;

    // Switch the client during render when auth presence changes. Deferring
    // this to an effect lets child effects issue one request through the old,
    // unauthenticated client during temporary-login and logout transitions.
    // Token refreshes keep the same client and flow through authRef instead.
    const client = React.useMemo(() => {
      const cache = new InMemoryCache({
        typePolicies: {
          GenericJSONDocument: {
            fields: {
              date: {
                merge(existing, incoming) {
                  return incoming ?? existing;
                }
              }
            }
          }
        }
      });

      const defaultOptions: any = {
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all'
        },
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all'
        }
      };

      if (!hasAuth) {
        return new ApolloClient({
          cache,
          uri: server.http,
          defaultOptions
        });
      }

      return new ApolloClient({
        cache,
        link: ApolloLink.from([errorLink, createWebConnection(server, authRef)]),
        defaultOptions
      });
    }, [server, hasAuth]);

   

    const value = {      
      client : client,    
    }

    return (
        <ApolloProvider client={client}>
            <ApolloAuthContext.Provider value={value}>
              {children}
            </ApolloAuthContext.Provider>
        </ApolloProvider>
    )
}
